import {
  PopoverBody,
  PopoverContent,
  useColorModeValue,
  HStack,
  IconButton,
  Tooltip,
  Box,
  Heading,
  VStack,
  Button,
  useToast,
} from '@chakra-ui/react';
import { Select } from 'chakra-react-select';
import {
  useMemo,
  SetStateAction,
  Dispatch,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { useMutation } from 'urql';
// Local imports
import { faPlus, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { getSelectStyles } from './style-overloads';
import {
  EventResource,
  EventType,
  OrderColumnDeepFragment,
  UpdateOrderDocument,
} from 'src/gql/graphql';
import SparkelIcon from 'src/components/common/icon/SparkelIcon';
import { useEventHistory } from 'src/hooks/event-history';

interface GroupPopOverProps {
  orderColumns: Array<OrderColumnDeepFragment>;
  grouping: Array<string>;
  projectId: string;
  orderId: string;
  setGrouping: Dispatch<SetStateAction<Array<string>>>;
  columnNameMapping?: Record<string, { id: string; newName: string }>;
}

interface EmptyState {
  shouldAlter: boolean;
  value: boolean;
}

export function GroupPopOver({
  grouping,
  orderColumns,
  projectId,
  orderId,
  setGrouping,
  columnNameMapping,
}: GroupPopOverProps) {
  const dropdownValueColor = useColorModeValue('gray.800', 'gray.50');
  const activeDropdownValueColor = useColorModeValue('teal.500', 'teal.200');
  const dropdownIndicatorColor = useColorModeValue('gray.300', 'gray.500');
  const hoverBackgroundColor = useColorModeValue('gray.50', 'gray.700');
  const selectedItemTextColor = useColorModeValue('blue.700', 'blue.200');
  const selectedItemBackgroundColor = useColorModeValue(
    'blue.50',
    'rgba(66, 153, 225, 0.24)'
  );
  const [showEmpty, setShowEmpty] = useState(() => grouping.length === 0);
  const [isLoading, setLoading] = useState(false);
  const toast = useToast();

  const [, updateOrder] = useMutation(UpdateOrderDocument);

  const { chakraStyles, activeSelectChakraStyles } = getSelectStyles(
    dropdownValueColor,
    dropdownIndicatorColor,
    selectedItemBackgroundColor,
    selectedItemTextColor,
    hoverBackgroundColor,
    activeDropdownValueColor
  );

  // Helper function to get column display name
  const getColumnDisplayName = useCallback(
    (column: OrderColumnDeepFragment) => {
      // If we have a mapping for this column, use the mapped name
      if (columnNameMapping && columnNameMapping[column.id]) {
        return columnNameMapping[column.id].newName;
      }
      return column.name;
    },
    [columnNameMapping]
  );

  // Define a standalone function to get standard ID from a column type
  // This doesn't use useCallback to avoid the initialization issue
  function getStandardIdFromColumnType(columnType: string): string {
    switch (columnType) {
      case 'DESCRIPTION':
        return 'title';
      case 'UNIT':
        return 'unit';
      case 'COLOR':
        return 'color';
      case 'QUANTITY':
        return 'quantity';
      case 'UNIT_PRICE':
        return 'unitPrice';
      case 'TOTAL':
        return 'total';
      case 'LINKED_COUNT':
        return 'linkedCount';
      case 'PARENT_ORDER':
        return 'parentOrder';
      case 'PARENT_ORDER_STATUS':
        return 'parentOrderStatus';
      default:
        return '';
    }
  }

  // Once one item has been chosen to populate one options list, it shouldn't be available to populate the next one.
  const { columns, availableColumns } = useMemo(() => {
    const columns: Array<{ label: string; value: string }> = [];
    const availableColumns: Array<{ label: string; value: string }> = [];

    // First, create a map of column IDs to their respective display names
    const columnDisplayNames = new Map<string, string>();

    orderColumns.forEach((col) => {
      // Get the proper display name for this column
      const displayName = getColumnDisplayName(col);
      columnDisplayNames.set(col.id, displayName);

      // For the dropdown, use the col.id (database ID) as the value
      // This ensures we can properly track renamed columns and find them later
      const columnValue = col.id;

      // Check if this column ID is in the grouping array
      // For standard columns, also check if their standardized ID (like "parentOrder") is in grouping
      let isInGrouping = grouping.includes(columnValue);

      // If not found by database ID, check standard ID for standard columns
      if (
        !isInGrouping &&
        col.columnType !== 'CUSTOM' &&
        col.columnType !== 'PARENT_ORDER_LABEL' &&
        col.columnType !== 'ITEM_GROUP'
      ) {
        // Get the standard ID for this column type
        const standardId = getStandardIdFromColumnType(col.columnType);
        isInGrouping = grouping.includes(standardId);
      }

      columns.push({ label: displayName, value: columnValue });
      if (!isInGrouping) {
        availableColumns.push({ label: displayName, value: columnValue });
      }
    });

    return { columns, availableColumns };
  }, [orderColumns, grouping, getColumnDisplayName]);

  const { logEvent } = useEventHistory(projectId);

  const updateGrouping = useCallback(
    async (newGrouping: Array<string>, emptyState: EmptyState) => {
      try {
        setLoading(true);

        // Create a mapping for standard columns to preserve standard IDs
        const standardIdMapping = new Map<string, string>();

        // For each grouping value that looks like a standard ID, save it
        for (const gId of grouping) {
          if (gId.indexOf('-') === -1) {
            // Standard IDs like "parentOrder" don't have hyphens
            // Find the corresponding column for this standard ID
            const matchingColumn = orderColumns.find(
              (col) => getStandardIdFromColumnType(col.columnType) === gId
            );

            if (matchingColumn) {
              // Map the database ID to the standard ID to preserve it
              standardIdMapping.set(matchingColumn.id, gId);
            }
          }
        }

        // Filter out any invalid column IDs before proceeding
        // Need to handle both database IDs and standard IDs
        const validColumnIds = newGrouping.filter((gId) => {
          // If it's a database ID (contains hyphens), check if it exists in orderColumns
          if (gId.indexOf('-') !== -1) {
            return orderColumns.some((col) => col.id === gId);
          }
          // If it's a standard ID (like "parentOrder"), check if any column has this standard ID
          else {
            return orderColumns.some(
              (col) => getStandardIdFromColumnType(col.columnType) === gId
            );
          }
        });

        // Convert the validColumnIds to their database representations
        const databaseColumnIds = validColumnIds.map((gId) => {
          // If it's already a database ID, use it as is
          if (gId.indexOf('-') !== -1) {
            return gId;
          }
          // If it's a standard ID, find the matching column and use its database ID
          const matchingColumn = orderColumns.find(
            (col) => getStandardIdFromColumnType(col.columnType) === gId
          );
          return matchingColumn ? matchingColumn.id : gId;
        });

        // Map column IDs to their database representation with name and type
        const aggregatedOrderGrouping = databaseColumnIds
          .map((gId) => {
            // Find the column object by ID
            const column = orderColumns.find((col) => col.id === gId);

            if (!column) {
              console.warn(
                `Column with ID ${gId} not found in orderColumns. Skipping.`
              );
              return null;
            }

            return {
              columnName: column.name,
              columnType: column.columnType,
            };
          })
          .filter(Boolean); // Remove any null entries

        const resp = await updateOrder({
          input: {
            id: orderId,
            patch: {
              aggregatedOrderGrouping,
            },
          },
        });

        if (resp.error) {
          console.error(resp.error);
          toast({
            title: 'Failed to save the grouping state',
            status: 'error',
          });
          return;
        }

        // For the UI state, preserve the original mix of standard IDs and database IDs
        setGrouping(validColumnIds);

        if (emptyState.shouldAlter) {
          setShowEmpty(emptyState.value);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);

        logEvent({
          eventName: 'Table Grouping Updated',
          eventType: EventType.Modified,
          resourceName: orderId,
          resourceType: EventResource.Order,
          metadata: {
            affectedResources: [orderId],
          },
        });
      }
    },
    [updateOrder, orderId, setGrouping, logEvent, grouping, orderColumns, toast]
  );

  // Helper function to find the column in the dropdown that corresponds to a grouping value
  // This handles both database IDs and standard IDs like "parentOrder"
  const findColumnForGrouping = useCallback(
    (
      groupValue: string,
      columnOptions: Array<{ label: string; value: string }>
    ): any => {
      // First try direct match (for database IDs)
      const directMatch = columnOptions.find((col) => col.value === groupValue);
      if (directMatch) {
        return directMatch;
      }

      // If no direct match and this looks like a standard ID (e.g., "parentOrder"),
      // find the column that has the matching standard ID
      if (groupValue.indexOf('-') === -1) {
        // Standard IDs don't have hyphens
        const matchingColumn = orderColumns.find((col) => {
          return getStandardIdFromColumnType(col.columnType) === groupValue;
        });

        if (matchingColumn) {
          // Now find this column in the options by its database ID
          return (
            columnOptions.find((col) => col.value === matchingColumn.id) || null
          );
        }
      }

      // No match found
      return null;
    },
    [orderColumns]
  );

  const { t } = useTranslation(['table', 'common']);

  // Reset showEmpty to false if grouping is added or removed
  useEffect(() => {
    // Only auto-hide the empty dropdown when we have no grouping
    // Otherwise, let the user control it with the "Then by" button
    if (grouping.length === 0 && !showEmpty) {
      setShowEmpty(true);
    }
  }, [grouping, showEmpty]);

  return (
    <PopoverContent shadow="lg" minWidth="3xs" width="fit-content">
      <PopoverBody>
        <Heading as={'h3'} size={'xs'} marginTop={1} marginBottom={3}>
          {t('table:grouping.title')}
        </Heading>
        <VStack>
          {grouping.map((cgroup, index) => (
            <HStack key={cgroup} width="100%">
              <Box minWidth={144} width="100%" maxWidth={200}>
                <Select
                  options={availableColumns}
                  menuPlacement="auto"
                  variant="filled"
                  chakraStyles={chakraStyles}
                  isDisabled={isLoading}
                  placeholder={t('table:grouping.assign.placeholder')}
                  value={findColumnForGrouping(cgroup, columns)}
                  onChange={(selected) => {
                    const groupingValue = (selected as any).value;
                    if (cgroup !== groupingValue) {
                      const newGrouping = grouping.toSpliced(
                        index,
                        1,
                        groupingValue
                      );
                      updateGrouping(newGrouping, {
                        shouldAlter: false,
                        value: false,
                      });
                    }
                  }}
                />
              </Box>
              <Tooltip label={t('table:grouping.remove.tooltip')}>
                <IconButton
                  size="xs"
                  aria-label="remove grouping"
                  colorScheme="gray"
                  variant="ghost"
                  isLoading={isLoading}
                  icon={<SparkelIcon icon={faTimes} color="inherit" />}
                  onClick={() => {
                    // Remove the specific grouping value preserving the index
                    const newGrouping = grouping.toSpliced(index, 1);
                    updateGrouping(newGrouping, {
                      shouldAlter: newGrouping.length <= 0,
                      value: newGrouping.length <= 0, // Only show empty if no grouping
                    });
                  }}
                />
              </Tooltip>
            </HStack>
          ))}

          {showEmpty && (
            <Box minWidth={144} width="100%" maxWidth={200}>
              <Select
                options={availableColumns}
                variant="filled"
                chakraStyles={chakraStyles}
                placeholder={t('table:grouping.assign.placeholder')}
                value={null}
                isDisabled={isLoading}
                onChange={(selected) => {
                  const groupingValue = (selected as any).value;
                  // Always append to existing grouping array
                  const newGrouping = [...grouping, groupingValue];

                  updateGrouping(newGrouping, {
                    shouldAlter: true,
                    value: false, // Hide empty after adding
                  });
                }}
              />
            </Box>
          )}
          {!showEmpty && (
            <Button
              marginTop={2}
              leftIcon={<SparkelIcon icon={faPlus} color="inherit" />}
              alignSelf="start"
              size="xs"
              variant="ghost"
              onClick={() => {
                // Show the empty dropdown for adding another grouping level
                setShowEmpty(true);
              }}
            >
              {t('table:grouping.then-by')}
            </Button>
          )}
        </VStack>
      </PopoverBody>
    </PopoverContent>
  );
}
